/**
 * Styles for RmcOrderList component
 * Contains extracted styles from RmcOrderList.tsx to improve readability and maintainability
 */

// Common colors from the design system
// In a real application, these would come from the theme
const colors = {
  border: '#e8ecf0', // theme.palette.divider
  background: {
    default: '#ffffff', // theme.palette.background.paper
    hover: '#f7f9fa', // theme.palette.grey[50]
    selected: '#e3f2fd', // theme.palette.primary.light
    tableHead: '#f0f2f5' // theme.palette.grey[100]
  },
  text: {
    primary: '#29333d', // theme.palette.text.primary
    secondary: '#52667a' // theme.palette.text.secondary
  },
  status: {
    success: '#00ab50', // theme.palette.success.main
    error: '#da0901' // theme.palette.error.main
  },
  button: {
    primary: {
      default: '#016ad4', // theme.palette.primary.main
      hover: '#0055b8', // theme.palette.primary.dark
      disabled: '#a5c4ec' // theme.palette.action.disabled
    },
    secondary: {
      border: '#e8ecf0', // theme.palette.divider
      hover: '#016ad4' // theme.palette.primary.main
    }
  }
}

// Interface for style props
export interface StyleProps {
  isMobile?: boolean
  isSelected?: boolean
}

/**
 * Get styles for the container
 */
export const getContainerStyles = ({isMobile}: StyleProps) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: isMobile ? '24px' : '32px'
})

/**
 * Get styles for section titles
 */
export const getSectionTitleStyles = ({isMobile}: StyleProps) => ({
  fontSize: isMobile ? '16px' : '18px',
  fontWeight: 600,
  lineHeight: '24px',
  color: colors.text.primary,
  fontFamily: 'Inter',
  marginBottom: '16px'
})

/**
 * Get styles for the table
 */
export const getTableStyles = () => ({
  width: '100%',
  tableLayout: 'fixed',
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${colors.border}`,
    padding: '8px',
    fontFamily: 'Inter',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: colors.background.tableHead,
    height: '44px',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '18px',
    color: colors.text.secondary
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    backgroundColor: colors.background.default,
    height: '84px',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '18px',
    color: colors.text.primary,
    verticalAlign: 'center'
  }
})

/**
 * Get styles for table rows
 */
export const getTableRowStyles = ({isSelected = false}: StyleProps) => ({
  cursor: 'pointer',
  backgroundColor: isSelected ? colors.background.selected : colors.background.default,
  border: isSelected ? `2px solid ${colors.button.primary.default}` : '1px solid transparent',
  '&:hover': {
    backgroundColor: isSelected ? colors.background.selected : colors.background.hover
  }
})

/**
 * Get styles for mobile order cards
 */
export const getOrderCardStyles = ({isSelected = false}: StyleProps) => ({
  cursor: 'pointer',
  backgroundColor: isSelected ? colors.background.selected : colors.background.default,
  border: isSelected ? `2px solid ${colors.button.primary.default}` : `1px solid ${colors.border}`,
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: isSelected ? colors.background.selected : colors.background.hover
  }
})

/**
 * Get styles for card content container
 */
export const getCardContentStyles = () => ({
  padding: '12px 16px'
})

/**
 * Get styles for add item button
 */
export const getAddItemButtonStyles = () => ({
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 500,
  color: colors.button.primary.default,
  borderColor: colors.button.secondary.border,
  backgroundColor: colors.background.default,
  '&:hover': {
    borderColor: colors.button.secondary.hover,
    backgroundColor: colors.background.hover
  },
  '&:disabled': {
    color: '#9ca3af',
    borderColor: '#e5e7eb',
    backgroundColor: '#f9fafb'
  }
})

/**
 * Get styles for contact details section
 */
export const getContactSectionStyles = ({isMobile}: StyleProps) => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  gap: isMobile ? '16px' : '24px'
})

/**
 * Get styles for place order button
 */
export const getPlaceOrderButtonStyles = () => ({
  width: '100%', // Full width on both mobile and desktop
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 500,
  padding: '10px 16px',
  backgroundColor: colors.button.primary.default,
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: colors.button.primary.hover
  },
  '&:disabled': {
    backgroundColor: colors.button.primary.disabled,
    color: '#ffffff'
  }
})
